import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

function Login() {
  const [terminal, setTerminal] = useState('');
  const [airline, setAirline] = useState('');
  const [airlineCode, setAirlineCode] = useState('');
  const [airlines, setAirlines] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [distinctAirlines, setDistinctAirlines] = useState([]);
  const [userRole, setUserRole] = useState('Eligibility Check');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const hasFetchedAirlines = useRef(false);

  // Load distinct airlines from local storage
  const loadDistinctAirlines = () => {
    const storedAirlines = JSON.parse(localStorage.getItem('airlines')) || [];
    const distinctAirlineNames = [...new Set(storedAirlines.map(item => item.sys_airlinename))];
    setDistinctAirlines(distinctAirlineNames);
  };

  // Fetch airlines from backend and store in local storage, fallback to localStorage if backend fails
  const fetchAirlines = async () => {
    try {
      const response = await fetch('https://starskan.com/api/airlines');
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched airlines data:', data);
        setAirlines(data);
        localStorage.setItem('airlines', JSON.stringify(data)); // Store airlines in local storage
        loadDistinctAirlines(); // Update distinct airlines after storing
        // Comment out airline dropdown selected code for now
        // if (data.length > 0) {
        //   setAirline(data[0].sys_airlinename);
        //   setAirlineCode(data[0].sys_airlinecode);
        // }
      } else {
        console.error('Failed to fetch airlines from backend');
        // Fallback to localStorage data if backend fails
        const storedAirlines = JSON.parse(localStorage.getItem('airlines')) || [];
        if (storedAirlines.length > 0) {
          setAirlines(storedAirlines);
          // Comment out airline dropdown selected code for now
          // if (storedAirlines.length > 0) {
          //   setAirline(storedAirlines[0].sys_airlinename);
          //   setAirlineCode(storedAirlines[0].sys_airlinecode);
          // }
        }
      }
    } catch (error) {
      console.error('Error fetching airlines:', error);
      // Fallback to localStorage data if backend fails
      const storedAirlines = JSON.parse(localStorage.getItem('airlines')) || [];
      if (storedAirlines.length > 0) {
        setAirlines(storedAirlines);
        // Comment out airline dropdown selected code for now
        // if (storedAirlines.length > 0) {
        //   setAirline(storedAirlines[0].sys_airlinename);
        //   setAirlineCode(storedAirlines[0].sys_airlinecode);
        // }
      }
    } finally {
      setLoading(false);
    }
  };
  
  // New function to fetch terminals from the new API endpoint
  const fetchTerminals = async () => {
    try {
      const response = await fetch('https://starskan.com/api/terminals');
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('terminals', JSON.stringify(data)); // Store all terminal details in localStorage

        // Extract distinct terminal names for dropdown
        const distinctTerminalNames = [...new Set(data.map(item => item.sys_terminal))];
        setTerminals(distinctTerminalNames);
        localStorage.setItem('terminalList', JSON.stringify(distinctTerminalNames)); // Store distinct terminal list in localStorage

        if (distinctTerminalNames.length > 0) {
          setTerminal(distinctTerminalNames[0]);
        }
      } else {
        console.error('Failed to fetch terminals from backend');
      }
    } catch (error) {
      console.error('Error fetching terminals:', error);
    }
  };

  useEffect(() => {
    // Clear all localStorage values when loading login screen except airlines
    const storedAirlines = localStorage.getItem('airlines');
    localStorage.clear();
    if (storedAirlines) {
      localStorage.setItem('airlines', storedAirlines);
    }

    // Load airlines from localStorage if available
    const airlinesData = JSON.parse(localStorage.getItem('airlines')) || [];
    if (airlinesData.length > 0) {
      setAirlines(airlinesData);
      loadDistinctAirlines();
    }

    // Fetch airlines and terminals from backend to get new values
    fetchAirlines();
    fetchTerminals();
  }, []);



  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Simple validation
    if (!username || !password) {
      setError('Please enter username and password');
      return;
    }

    // Integrate with backend login API
    try {
      const response = await fetch('https://starskan.com/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password, userRole }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.token);
        localStorage.setItem('userRole', userRole);
        localStorage.setItem('terminal', terminal);
        localStorage.setItem('airline', airline);
        localStorage.setItem('airlineCode', airlineCode);
        localStorage.setItem('companyCode', data.companyCode || '');
        localStorage.setItem('userId', data.userId || '');
        
        // Navigate based on user role
        if (userRole === 'Eligibility Check') {
          navigate('/questions'); // Skip Welcome page for Eligibility Check
        } else if (userRole === 'HBR & Scanning') {
          navigate('/consent'); // Directly to scanning page barcode-pdf417-scanner
        } else {
          navigate('/welcome');
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Invalid username or password');
      }
    } catch (error) {
      setError('Error connecting to the server');
    }
  };

  return (
    <div style={{ 
      maxWidth: 400, 
      margin: 'auto', 
      padding: 20, 
      backgroundColor: '#f0f0f5', 
      borderRadius: 10, 
      fontFamily: "'Arial Black', Arial, sans-serif", 
      color: '#000', 
      textAlign: 'center', 
      boxShadow: '0 0 10px rgba(0,0,0,0.1)' 
    }}>
      <img
        src="/arl-logo.png"
        alt="ARL Aviation Services Logo"
        style={{ width: '100%', maxWidth: 250, marginBottom: 40, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
      />
      <h3 style={{ 
        color: '#000', 
        textAlign: 'center', 
        marginBottom: 20, 
        fontWeight: 'bold', 
        fontSize: 24, 
        fontFamily: "'Arial Black', Arial, sans-serif" 
      }}>Welcome to ARL Aviation Services Star Skan Application</h3>
      <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
      {/* Removed Select Airline dropdown as per user request */}
      {/* <div>
        <label style={{ fontWeight: 'bold' }}>Select Airline:</label><br />
        <select
          value={airline}
          onChange={(e) => setAirline(e.target.value)}
          style={{
            width: '100%',
            padding: 8,
            borderRadius: 6,
            border: '1px solid #ccc',
            backgroundColor: '#f0f0f5',
            color: '#000',
            fontWeight: 'bold',
            fontSize: 16,
            marginBottom: 10,
            fontFamily: "'Arial Black', Arial, sans-serif",
            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
        {distinctAirlines.map((airlineName) => (
            <option key={airlineName} value={airlineName}>
              {airlineName}
            </option>
          ))}
        </select>
      </div> */}
        <div>
          <label style={{ fontWeight: 'bold' }}>Select Terminal:</label><br />
          <select
            value={terminal}
            onChange={(e) => setTerminal(e.target.value)}
            style={{
              width: '100%',
              padding: 8,
              borderRadius: 6,
              border: '1px solid #ccc',
              backgroundColor: '#f0f0f5',
              color: '#000',
              fontWeight: 'bold',
              fontSize: 16,
              marginBottom: 10,
              fontFamily: "'Arial Black', Arial, sans-serif",
              boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
            {terminals.map((terminalName) => (
              <option key={terminalName} value={terminalName}>
                {terminalName}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ fontWeight: 'bold' }}>User Role:</label><br />
          <select
            value={userRole}
            onChange={(e) => setUserRole(e.target.value)}
            style={{
              width: '100%',
              padding: 8,
              borderRadius: 6,
              border: '1px solid #ccc',
              backgroundColor: '#f0f0f5',
              color: '#000',
              fontWeight: 'bold',
              fontSize: 16,
              marginBottom: 10,
              fontFamily: "'Arial Black', Arial, sans-serif",
              boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
          <option value="Eligibility Check">Eligibility Check</option>
          <option value="HBR & Scanning">HBR & Scanning</option>
          </select>
        </div>
        <div>
          <label style={{ fontWeight: 'bold' }}>Username:</label><br />
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', backgroundColor: '#fff', color: '#000', fontWeight: 'normal', fontSize: 16, marginLeft: 0, marginRight: 2 }}
          />
        </div>
        <div style={{ marginTop: 10 }}>
          <label style={{ fontWeight: 'bold' }}>Password:</label><br />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', backgroundColor: '#fff', color: '#000', fontWeight: 'normal', fontSize: 16, marginLeft: 0, marginRight: 2 }}
          />
        </div>
        {error && <div style={{ color: '#d32f2f', marginTop: 10, fontWeight: 'bold' }}>{error}</div>}
        <button type="submit" style={{ marginTop: 20, backgroundColor: '#2e7d32', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 6, cursor: 'pointer', fontWeight: 'bold', fontSize: 18, display: 'block', marginLeft: 'auto', marginRight: 'auto', width: '100%' }}>
          Login
        </button>
      </form>
    </div>
  );
}

export default Login;
